import { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/auth-context";
import { getProjects, deleteProject } from "@/services/project-service";
import { ConfirmDialog, Pagination } from "@/components/common";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { Project } from "@/types/project";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import icArrowLeft from "@/assets/icons/ic-arrow-left.svg";
import icArrowPlus from "@/assets/icons/ic-plus-pr.svg";
import IlNoData from "@/assets/icons/il-no-data.svg";
import IconNoDataMobile from "@/assets/icons/icon-nodata-mobile.svg";
import { STATUS_CONFIG } from "@/lib/utils";

const ITEMS_PER_PAGE = 10;

export default function ProjectListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [projects, setProjects] = useState<Project[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const showBackButton = location.state?.from === "my-page";
  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  const fetchProjects = useCallback(async (page: number) => {
    setIsLoading(true);
    try {
      const result = await getProjects(page, ITEMS_PER_PAGE);
      setProjects(result.data.data!.items);
      setTotal(result.data.data!.total);
    } catch {
      setProjects([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects(currentPage);
  }, [currentPage, fetchProjects]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteProject(deleteTarget);
      toast({ description: "프로젝트가 삭제되었습니다." });
      setDeleteTarget(null);
      if (total <= 1) {
        navigate("/my-page");
      } else if (projects.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchProjects(currentPage);
      }
    } catch (e) {
      // silently fail
      console.error(e);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
      d.getDate(),
    )} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  const getStatusBadge = (status: string) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.init;
    return (
      <div
        className={`inline-flex shrink-0 items-center gap-1 text-black justify-center rounded-[4px] leading-5 py-[2px] text-[14px] tracking-[-0.01em] ${config.bg} ${config.text} ${config.padding}`}
      >
        <p>{config.label}</p>
      </div>
    );
  };

  const canEdit = (status: string) => status === "in_progress" || status === "under_review";
  const canViewReport = (project: Project) => project.has_report && project.status !== "cancelled";

  return (
    <>
      {isLoading && (
        <div className="flex items-center justify-center py-20 h-[60vh] w-full">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-black/10 border-t-[#FF3B00]" />
        </div>
      )}
      <div 
        className="flex flex-col items-center gap-6 max-w-[1120px] px-5 min-[1120px]:px-0 mx-auto px-5 py-6 lg:py-10 w-full"
        style={{ display: isLoading ? "none" : "flex" }}
      >
        {/* Header */}
      <div className="flex gap-4 w-full flex-col lg:flex-row lg:items-center">
        {showBackButton && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/my-page")}
            className="w-12 h-12"
          >
            <img src={icArrowLeft} alt="Icon Arrow Left" />
          </Button>
        )}
        <h1 className="flex-1 text-xl font-bold leading-6 tracking-[-0.018em] text-[#000] whitespace-pre-line break-all">
          {user?.requester_name || user?.email?.split("@")[0] || "User"}님의 프로젝트
        </h1>
        <Button
          variant="default"
          onClick={() => navigate("/chat")}
          className="inline-flex items-center rounded gap-1 h-10 lg:h-12 px-4 lg:px-5 py-2 lg:py-3 text-sm lg:text-base font-bold  w-[120px] lg:w-[146px]"
        >
          <img src={icArrowPlus} alt="" />새 프로젝트
        </Button>
      </div>

      {/* Content */}
      {/* Desktop Table */}
          <div className="hidden lg:block w-full rounded-[4px] border border-black/[0.09] overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border border-[#00000017] hover:bg-transparent">
                  <TableHead className="bg-[#EBEBEB] text-xs font-normal uppercase text-[#000000] h-auto py-3 px-3 w-[310px]">
                    프로젝트명
                  </TableHead>
                  <TableHead className="bg-[#EBEBEB] text-xs font-normal uppercase text-[#000000] h-auto py-3 px-3 w-[274px]">
                    생성일
                  </TableHead>
                  <TableHead className="bg-[#EBEBEB] text-xs font-normal uppercase text-[#000000] h-auto py-3 px-3">
                    상태
                  </TableHead>
                  <TableHead className="bg-[#EBEBEB] text-xs font-normal uppercase text-[#000000] h-auto py-3 px-3 w-[135px]">
                    보고서 열람
                  </TableHead>
                  <TableHead className="bg-[#EBEBEB] text-xs font-normal uppercase text-[#000000] h-auto py-3 px-3 text-right">
                    관리
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {projects.length === 0 ? (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={5} className="border-0">
                      <div className="flex flex-col items-center justify-center gap-6 pt-20 pb-[300px]">
                        <img src={IlNoData} alt="No data" />
                        <div className="flex flex-col items-center gap-1">
                          <p className="text-[32px] leading-[40px] tracking-[-0.03em] font-normal text-[#000000]">
                            No data
                          </p>
                          <p className="text-base text-[#000000]">
                            아직 생성된 프로젝트가 없습니다
                          </p>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {" "}
                    {projects.map((project) => (
                      <TableRow
                        key={project.id}
                        className="border-b border-black/[0.09] last:border-b-0 h-12"
                      >
                        <TableCell className="px-3 py-3 max-w-[310px]">
                          <span
                            className="block truncate text-base text-[#000000]"
                            title={project.name || ""}
                          >
                            {project.name || "-"}
                          </span>
                        </TableCell>
                        <TableCell className="px-3 py-3 text-base text-[#000000]">
                          {formatDate(project.created_at)}
                        </TableCell>
                        <TableCell className="px-3 py-3">
                          {getStatusBadge(project.status)}
                        </TableCell>
                        <TableCell className="px-3 py-3">
                          {canViewReport(project) ? (
                            <button
                              type="button"
                              className="text-base font-bold text-[rgba(0,0,0,0.91)] underline"
                              onClick={() =>
                                navigate(`/projects/${project.id}/report`, {
                                  state: { from: "list" },
                                })
                              }
                            >
                              보기
                            </button>
                          ) : (
                            <span className="text-base text-[#8D8D8D]">-</span>
                          )}
                        </TableCell>
                        <TableCell className="px-3 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => setDeleteTarget(project.id)}
                              className="w-[80px] rounded-[4px] bg-[#F7DDCF] px-3 py-2 text-xs font-bold text-[#000000]  transition-colors"
                            >
                              삭제
                            </button>
                            {canEdit(project.status) ? (
                              <button
                                type="button"
                                onClick={() =>
                                  navigate(`/projects/${project.id}/report`, {
                                    state: { from: "list", mode: "edit" },
                                  })
                                }
                                className="w-[80px] rounded-[4px] bg-[#EBEBEB] px-3 py-2 text-xs font-bold text-[#000000] backdrop-blur-[40px]  transition-colors"
                              >
                                수정
                              </button>
                            ) : (
                              <div className="w-[80px]" />
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Card List */}
          <div className="flex flex-col gap-6 w-full lg:hidden">
            {projects.length === 0 && (
              <div className="flex flex-col items-center justify-center gap-6 pt-20 pb-[300px] border border-[#00000027]">
                <img src={IconNoDataMobile} alt="No data" />
                <div className="flex flex-col items-center gap-1">
                  <p className="text-[32px] leading-[40px] tracking-[-0.03em] font-normal text-[#000000]">
                    No data
                  </p>
                  <p className="text-base text-[#000000]">아직 생성된 프로젝트가 없습니다</p>
                </div>
              </div>
            )}
            {projects.map((project) => (
              <div
                key={project.id}
                className="flex flex-col gap-3 rounded border border-[rgba(0,0,0,0.15)] bg-white p-3 pb-4"
              >
                {/* Top: project name + date + status */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex flex-col gap-0.5 min-w-0 max-w-[232px]">
                    <span className="text-base font-semibold leading-6 tracking-[-0.01em] text-black truncate">
                      {project.name || "-"}
                    </span>
                    <span className="text-base text-[#000000]">
                      {formatDate(project.created_at)}
                    </span>
                  </div>
                  {getStatusBadge(project.status)}
                </div>
                {/* Divider */}
                <div className="h-px bg-[rgba(0,0,0,0.15)]" />
                {/* Bottom: view report + actions */}
                <div className="flex items-center justify-between">
                  {canViewReport(project) ? (
                    <button
                      type="button"
                      className="text-base font-bold text-[#000000] underline"
                      onClick={() =>
                        navigate(`/projects/${project.id}/report`, { state: { from: "list" } })
                      }
                    >
                      보기
                    </button>
                  ) : (
                    <span className="text-base text-[#8D8D8D]">-</span>
                  )}

                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setDeleteTarget(project.id)}
                      className="w-[80px] rounded h-8 bg-[#FFAA72] text-xs font-bold text-[#000000]"
                    >
                      삭제
                    </button>
                    {canEdit(project.status) && (
                      <button
                        type="button"
                        onClick={() =>
                          navigate(`/projects/${project.id}/report`, {
                            state: { from: "list", mode: "edit" },
                          })
                        }
                        className="w-[80px] rounded h-8 bg-[#EBEBEB] text-xs font-bold text-[#000000] backdrop-blur-[20px]"
                      >
                        수정
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        iconVariant="warning"
        title="프로젝트 삭제 확인"
        description="해당 프로젝트를 삭제하시겠습니까?"
        primaryAction={{ label: "네", onClick: handleDelete }}
        secondaryAction={{
          label: "아니요",
          onClick: () => setDeleteTarget(null),
        }}
      />
    </div>
    </>
  );
}
